import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, SignIn, EnvelopeSimple } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { LFSPanel } from './LFSPanel';
import { FriendsPanel } from './FriendsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RoomLobby = ({ game, onRoomJoined, user }) => {
  const [mode, setMode] = useState(null); // null | 'create' | 'join'
  const [joinCode, setJoinCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [friendsRefreshKey, setFriendsRefreshKey] = useState(0);

  const emailVerified = user?.email_verified;

  const bumpFriendsRefresh = () => setFriendsRefreshKey((n) => n + 1);

  // Server-side metadata — never call RAWG directly from the browser.
  useEffect(() => {
    if (!game?.id) return;
    let cancelled = false;
    axios
      .get(`${API}/games/metadata/${encodeURIComponent(game.id)}`)
      .then((res) => {
        if (!cancelled) setMetadata(res.data);
      })
      .catch(() => {
        /* non-fatal — lobby still renders with base game info */
      });
    return () => {
      cancelled = true;
    };
  }, [game?.id]);

  const createRoom = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/rooms/create`, { game_type: game.id });
      toast.success(`Room created! Code: ${response.data.code}`);
      onRoomJoined(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room');
    } finally {
      setLoading(false);
    }
  };

  const joinRoom = async () => {
    if (!joinCode.trim()) {
      toast.error('Enter a room code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/rooms/join`, {
        code: joinCode.trim().toUpperCase(),
      });
      toast.success('Joined room!');
      onRoomJoined(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join room');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-start overflow-y-auto bg-[#FAFAFA] p-8" data-testid="room-lobby">
      <div className="w-full max-w-md">
        <div className="text-center mb-8" data-testid="room-lobby-header">
          <h2 className="font-heading text-3xl font-bold text-[#09090B] mb-2">{game.name}</h2>
          <p className="text-[#71717A] text-sm">{game.description}</p>

          {metadata?.tags?.length > 0 && (
            <div
              className="flex flex-wrap gap-1.5 justify-center mt-3"
              data-testid="lobby-game-tags"
            >
              {metadata.tags.slice(0, 6).map((t) => (
                <span
                  key={t.id ?? t.slug ?? t.name}
                  className="text-[10px] uppercase tracking-wider bg-[#F4F4F5] text-[#52525B] px-2 py-0.5 rounded"
                >
                  {t.name}
                </span>
              ))}
            </div>
          )}

          {metadata?.themed_emojis?.length > 0 && (
            <div
              className="mt-3 text-lg"
              data-testid="lobby-themed-emojis"
              aria-label="Themed emojis for this game"
            >
              {metadata.themed_emojis.slice(0, 6).join(' ')}
            </div>
          )}
        </div>

        {!emailVerified && (
          <div
            className="bg-yellow-50 border border-yellow-200 p-6 text-center"
            data-testid="verify-required-block"
          >
            <EnvelopeSimple size={32} weight="fill" className="text-yellow-600 mx-auto mb-3" />
            <h3 className="font-heading text-lg font-bold text-yellow-900 mb-1">
              Verify Your Email to Play
            </h3>
            <p className="text-sm text-yellow-800">
              Multiplayer rooms are locked until you verify{' '}
              <span className="font-mono">{user?.email}</span>. Check your inbox for the
              verification link, or use the &quot;Resend Email&quot; button in the banner above.
            </p>
          </div>
        )}

        {emailVerified && !mode && (
          <div className="space-y-3">
            <Button 
              onClick={() => setMode('create')} 
              className="w-full bg-[#0047FF] hover:bg-[#0037CC] text-white py-6"
              data-testid="create-room-button"
            >
              <Plus size={20} className="mr-2" />
              Create New Room
            </Button>
            <Button 
              onClick={() => setMode('join')}
              variant="outline"
              className="w-full border-[#E4E4E7] text-[#09090B] py-6"
              data-testid="join-room-button"
            >
              <SignIn size={20} className="mr-2" />
              Join with Code
            </Button>
          </div>
        )}

        {emailVerified && mode === 'create' && (
          <div className="bg-white border border-[#E4E4E7] p-6 space-y-4">
            <div>
              <h3 className="font-medium text-[#09090B] mb-1">Create a new room</h3>
              <p className="text-sm text-[#71717A]">A 6-character code will be generated. Share it with a friend!</p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={createRoom} 
                disabled={loading}
                className="flex-1 bg-[#0047FF] hover:bg-[#0037CC] text-white"
                data-testid="confirm-create-room"
              >
                {loading ? 'Creating...' : 'Create Room'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setMode(null)}
                className="border-[#E4E4E7]"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {emailVerified && mode === 'join' && (
          <div className="bg-white border border-[#E4E4E7] p-6 space-y-4">
            <div>
              <Label htmlFor="room-code">Room Code</Label>
              <Input
                id="room-code"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={6}
                className="border-[#E4E4E7] font-mono text-lg tracking-widest text-center uppercase"
                data-testid="room-code-input"
                onKeyPress={(e) => e.key === 'Enter' && joinRoom()}
              />
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={joinRoom} 
                disabled={loading || joinCode.length !== 6}
                className="flex-1 bg-[#0047FF] hover:bg-[#0037CC] text-white"
                data-testid="confirm-join-room"
              >
                {loading ? 'Joining...' : 'Join Room'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setMode(null)}
                className="border-[#E4E4E7]"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 w-full max-w-md space-y-6">
        <FriendsPanel
          game={game}
          user={user}
          disabled={!emailVerified}
          refreshKey={friendsRefreshKey}
        />
        <LFSPanel
          game={game}
          user={user}
          disabled={!emailVerified}
          onFavouritesChanged={bumpFriendsRefresh}
        />
      </div>
    </div>
  );
};
